/**
 * Invoice List Page - Consultant
 * Displays list of invoices for consultant users
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Search, Filter as FilterIcon } from 'lucide-react';
import { mockInvoices } from '../../data/payments';
import { InvoiceCard } from '../../components/payment';
import { EmptyState } from '../../components/common/EmptyState';
import type { Invoice } from '../../types/payment';
import { InvoiceStatus } from '../../types/payment';
import { formatCurrency } from '../../utils/formatting';

export const InvoiceListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'ALL'>('ALL');

  // Filter invoices for consultant (org-002 - Halal Excellence Consultancy)
  const consultantInvoices = useMemo(() => {
    return mockInvoices.filter((invoice) => invoice.billFrom.id === 'org-002');
  }, []);

  // Apply filters
  const filteredInvoices = useMemo(() => {
    return consultantInvoices.filter((invoice) => {
      const matchesSearch =
        invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.billTo.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'ALL' || invoice.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [consultantInvoices, searchTerm, statusFilter]);

  // Calculate statistics
  const statistics = useMemo(() => {
    return {
      total: consultantInvoices.length,
      draft: consultantInvoices.filter((inv) => inv.status === InvoiceStatus.DRAFT).length,
      issued: consultantInvoices.filter(
        (inv) => inv.status === InvoiceStatus.ISSUED || inv.status === InvoiceStatus.SENT
      ).length,
      paid: consultantInvoices.filter((inv) => inv.status === InvoiceStatus.PAID).length,
      overdue: consultantInvoices.filter(
        (inv) =>
          inv.status !== InvoiceStatus.PAID &&
          inv.status !== InvoiceStatus.CANCELLED &&
          new Date(inv.dueDate) < new Date()
      ).length,
      totalRevenue: consultantInvoices
        .filter((inv) => inv.status === InvoiceStatus.PAID)
        .reduce((sum, inv) => sum + inv.totalAmount, 0),
      totalPending: consultantInvoices
        .filter(
          (inv) =>
            inv.status === InvoiceStatus.ISSUED ||
            inv.status === InvoiceStatus.SENT ||
            new Date(inv.dueDate) >= new Date()
        )
        .reduce((sum, inv) => sum + inv.totalAmount, 0),
    };
  }, [consultantInvoices]);

  const handleInvoiceClick = (invoice: Invoice) => {
    navigate(`/consultant/invoices/${invoice.id}`);
  };

  const handleCreateInvoice = () => {
    navigate('/consultant/invoices/new');
  };

  const handleDownloadInvoice = (invoice: Invoice) => {
    // Mock download functionality
    console.log('Downloading invoice:', invoice.invoiceNumber);
    alert(`Downloading invoice ${invoice.invoiceNumber}`);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    navigate(`/consultant/invoices/${invoice.id}`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('payments:invoice.myInvoices')}</h1>
          <p className="text-gray-600 mt-1">{t('payments:invoice.manageInvoicesSubtitle')}</p>
        </div>
        <button
          onClick={handleCreateInvoice}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          {t('payments:invoice.createInvoice')}
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">{t('payments:invoice.totalRevenue')}</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {formatCurrency(statistics.totalRevenue, 'MYR')}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {statistics.paid} {t('payments:invoice.paidInvoices')}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">{t('payments:invoice.totalPending')}</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">
            {formatCurrency(statistics.totalPending, 'MYR')}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {statistics.issued} {t('payments:invoice.issuedInvoices')}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">{t('payments:invoice.overdue')}</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{statistics.overdue}</div>
          <div className="text-xs text-gray-500 mt-1">{t('payments:invoice.requiresAttention')}</div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">{t('payments:invoice.drafts')}</div>
          <div className="text-2xl font-bold text-gray-600 mt-1">{statistics.draft}</div>
          <div className="text-xs text-gray-500 mt-1">{t('payments:invoice.notYetIssued')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex gap-4 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('payments:invoice.searchInvoices')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-[180px]">
            <div className="relative">
              <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'ALL')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="ALL">{t('payments:invoice.allStatuses')}</option>
                <option value={InvoiceStatus.DRAFT}>
                  {t(`payment.invoiceStatus.${InvoiceStatus.DRAFT.toLowerCase()}`)}
                </option>
                <option value={InvoiceStatus.ISSUED}>
                  {t(`payment.invoiceStatus.${InvoiceStatus.ISSUED.toLowerCase()}`)}
                </option>
                <option value={InvoiceStatus.SENT}>
                  {t(`payment.invoiceStatus.${InvoiceStatus.SENT.toLowerCase()}`)}
                </option>
                <option value={InvoiceStatus.PAID}>
                  {t(`payment.invoiceStatus.${InvoiceStatus.PAID.toLowerCase()}`)}
                </option>
                <option value={InvoiceStatus.OVERDUE}>
                  {t(`payment.invoiceStatus.${InvoiceStatus.OVERDUE.toLowerCase()}`)}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-3 text-sm text-gray-600">
          {t('payments:invoice.showingResults', {
            count: filteredInvoices.length,
            total: consultantInvoices.length,
          })}
        </div>
      </div>

      {/* Invoice List */}
      {filteredInvoices.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              onClick={() => handleInvoiceClick(invoice)}
              onDownload={() => handleDownloadInvoice(invoice)}
              onView={() => handleViewInvoice(invoice)}
              showActions
            />
          ))}
        </div>
      ) : (
        consultantInvoices.length === 0 ? (
          <EmptyState
            icon={<FileText className="w-10 h-10" />}
            title="No Invoices Yet"
            description="You haven't created any invoices yet. Start by creating your first invoice for your consulting services."
            action={{
              label: 'Create First Invoice',
              onClick: handleCreateInvoice,
              icon: <Plus className="w-5 h-5 mr-2" />,
            }}
          />
        ) : (
          <EmptyState
            icon={<FilterIcon className="w-10 h-10" />}
            title="No Matching Invoices"
            description="No invoices match your current search and filter criteria. Try adjusting the filters or search term."
            action={{
              label: 'Clear All Filters',
              onClick: () => {
                setSearchTerm('');
                setStatusFilter('ALL');
              },
            }}
          />
        )
      )}
    </div>
  );
};
